import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  FiActivity,
  FiArrowLeft,
  FiBookOpen,
  FiCalendar,
  FiClock,
  FiMail,
  FiPhone,
  FiTrendingUp,
  FiUsers,
  FiLayers,
} from 'react-icons/fi'
import staffTeachingApi from '../../api/staffTeachingApi'
import './StudentDetail.css'

const statusLabels = {
  present: 'Present',
  attended: 'Present',
  absent: 'Absent',
  excused: 'Excused',
  leave: 'Excused',
  unknown: 'Not Marked',
  not_marked: 'Not Marked',
}

const prettify = (value, fallback = '—') => {
  if (!value) return fallback
  return value
    .toString()
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ')
}

const formatDateOnly = (value) => {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const formatDateTime = (value) => {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatDuration = (start, end) => {
  if (!start || !end) return '—'
  const startTime = new Date(start)
  const endTime = new Date(end)
  if (Number.isNaN(startTime) || Number.isNaN(endTime)) return '—'
  const minutes = Math.max(0, Math.round((endTime - startTime) / 60000))
  return `${Math.max(1, minutes)} min`
}

const getInitials = (name, fallback = '?') => {
  if (!name) return fallback
  const segments = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
  if (!segments.length) return fallback
  return segments.map((segment) => segment[0]?.toUpperCase() || '').join('') || fallback
}

const StudentDetail = () => {
  const { studentId } = useParams()
  const [detail, setDetail] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const loadDetail = useCallback(async () => {
    if (!studentId) {
      setError('Missing student reference')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const payload = await staffTeachingApi.fetchStudentDetail(studentId)
      setDetail(payload)
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Failed to load student'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [studentId])

  useEffect(() => {
    loadDetail()
  }, [loadDetail])

  const student = detail?.student
  const fullName = useMemo(() => {
    if (!student) return ''
    const name = [student.firstName, student.lastName].filter(Boolean).join(' ')
    return name || student.email || 'Unnamed student'
  }, [student])

  const attendance = detail?.attendance || {}
  const enrollments = detail?.enrollments || []
  const upcomingLectures = detail?.upcomingLectures || []
  const recentLectures = detail?.recentLectures || []

  const attendanceCards = useMemo(() => {
    return [
      {
        label: 'Total sessions',
        value: attendance.totalSessions ?? 0,
        icon: FiActivity,
      },
      {
        label: 'Attended',
        value: attendance.attendedSessions ?? 0,
        icon: FiUsers,
      },
      {
        label: 'Absent',
        value: attendance.absentSessions ?? 0,
        icon: FiCalendar,
      },
      {
        label: 'Attendance rate',
        value: attendance.attendanceRate != null ? `${attendance.attendanceRate}%` : '—',
        icon: FiTrendingUp,
      },
    ]
  }, [attendance])

  const overviewCards = useMemo(() => {
    return [
      {
        label: 'Enrolled batches',
        value: detail?.meta?.enrolledBatchCount ?? enrollments.length,
        icon: FiLayers,
      },
      {
        label: 'Upcoming sessions',
        value: upcomingLectures.length,
        icon: FiCalendar,
      },
      {
        label: 'Completed sessions',
        value: attendance.attendedSessions ?? 0,
        icon: FiBookOpen,
      },
      {
        label: 'Last attendance update',
        value: formatDateTime(attendance.lastUpdated),
        icon: FiClock,
      },
    ]
  }, [attendance, detail?.meta?.enrolledBatchCount, enrollments.length, upcomingLectures.length])

  const lastSyncedLabel = detail?.updatedAt
    ? new Date(detail.updatedAt).toLocaleString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        month: 'short',
        day: 'numeric',
      })
    : 'Not synced yet'

  return (
    <div className="student-detail-page">
      <header className="student-detail-hero">
        <div className="hero-left">
          <Link to="/students" className="back-link">
            <FiArrowLeft size={16} /> Back to students
          </Link>

          <div className="student-header">
            <div className="student-avatar is-large">
              {student?.avatarUrl ? (
                <img src={student.avatarUrl} alt={fullName} loading="lazy" />
              ) : (
                <span>{getInitials(fullName)}</span>
              )}
            </div>
            <div>
              <h1>{fullName}</h1>
              <div className="student-contact-meta">
                {student?.email && (
                  <span>
                    <FiMail size={14} /> {student.email}
                  </span>
                )}
                {student?.phone && (
                  <span>
                    <FiPhone size={14} /> {student.phone}
                  </span>
                )}
              </div>
              <div className={`student-status-pill status-${(student?.status || 'active').toLowerCase()}`}>
                {prettify(student?.status || 'active')}
              </div>
            </div>
          </div>
        </div>

        <div className="hero-right">
          <div className="synced-badge">Last synced · {lastSyncedLabel}</div>
        </div>
      </header>

      <section className="student-detail-overview">
        {overviewCards.map((card) => {
          const Icon = card.icon
          return (
            <article key={card.label} className="overview-card">
              <div className="overview-icon">
                <Icon size={18} />
              </div>
              <div>
                <p>{card.label}</p>
                <strong>{card.value}</strong>
              </div>
            </article>
          )
        })}
      </section>

      <section className="student-attendance">
        <header>
          <h2>Attendance summary</h2>
          <p>Track lecture participation across assigned batches</p>
        </header>
        <div className="attendance-grid">
          {attendanceCards.map((card) => {
            const Icon = card.icon
            return (
              <article key={card.label} className="attendance-card">
                <div className="attendance-icon">
                  <Icon size={18} />
                </div>
                <div>
                  <p>{card.label}</p>
                  <strong>{card.value}</strong>
                </div>
              </article>
            )
          })}
        </div>
      </section>

      <section className="student-enrollments">
        <header>
          <h2>Batch enrollment</h2>
          <p>Review batch placement, timeline, and attendance health</p>
        </header>

        {isLoading && !enrollments.length ? (
          <div className="student-state">Loading batches…</div>
        ) : enrollments.length === 0 ? (
          <div className="student-state">No batch assignments detected.</div>
        ) : (
          <div className="enrollment-table-wrapper">
            <table className="enrollment-table">
              <thead>
                <tr>
                  <th scope="col">Batch</th>
                  <th scope="col">Course</th>
                  <th scope="col">Status</th>
                  <th scope="col">Joined</th>
                  <th scope="col">Attendance</th>
                  <th scope="col">Rate</th>
                </tr>
              </thead>
              <tbody>
                {enrollments.map((enrollment) => (
                  <tr key={enrollment.batchId}>
                    <td>
                      <div className="table-primary">{enrollment.batchName}</div>
                    </td>
                    <td>{enrollment.courseTitle || '—'}</td>
                    <td>
                      <span className={`status-pill status-${(enrollment.status || 'unknown').toLowerCase()}`}>
                        {prettify(enrollment.status || 'unknown')}
                      </span>
                    </td>
                    <td>{formatDateOnly(enrollment.joinedAt)}</td>
                    <td>
                      {enrollment.attendance?.totalSessions ?? 0} sessions ·{' '}
                      {enrollment.attendance?.attendedSessions ?? 0} attended
                    </td>
                    <td>
                      {enrollment.attendance?.attendanceRate != null
                        ? `${enrollment.attendance.attendanceRate}%`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="student-lectures">
        <div className="lecture-column">
          <header>
            <h2>Upcoming sessions</h2>
            <p>Sessions scheduled for this learner&apos;s batches</p>
          </header>
          {upcomingLectures.length === 0 ? (
            <div className="student-state">No upcoming sessions scheduled.</div>
          ) : (
            <ul className="lecture-list">
              {upcomingLectures.map((lecture) => (
                <li key={lecture.lectureId}>
                  <div className="lecture-header">
                    <strong>{lecture.topic}</strong>
                    <span className={`status-pill status-${(lecture.status || 'scheduled').toLowerCase()}`}>
                      {prettify(lecture.status || 'scheduled')}
                    </span>
                  </div>
                  <div className="lecture-meta">
                    <span>
                      <FiCalendar size={14} /> {formatDateTime(lecture.startTime)}
                    </span>
                    <span>
                      <FiClock size={14} /> {formatDuration(lecture.startTime, lecture.endTime)}
                    </span>
                  </div>
                  <div className="lecture-batch">
                    {lecture.batch?.batchName || 'Unassigned batch'} ·{' '}
                    {lecture.batch?.courseTitle || 'Course TBC'}
                  </div>
                  {lecture.moduleTitle && <div className="lecture-module">Module · {lecture.moduleTitle}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="lecture-column">
          <header>
            <h2>Recent sessions</h2>
            <p>Latest attendance outcomes for this learner</p>
          </header>
          {recentLectures.length === 0 ? (
            <div className="student-state">No attendance records yet.</div>
          ) : (
            <ul className="lecture-list">
              {recentLectures.map((lecture) => {
                const statusKey = (lecture.attendanceStatus || 'unknown').toLowerCase()
                return (
                  <li key={lecture.lectureId}>
                    <div className="lecture-header">
                      <strong>{lecture.topic}</strong>
                      <span className={`attendance-pill status-${statusKey}`}>
                        {statusLabels[statusKey] || statusLabels.unknown}
                      </span>
                    </div>
                    <div className="lecture-meta">
                      <span>
                        <FiCalendar size={14} /> {formatDateTime(lecture.startTime)}
                      </span>
                      <span>
                        <FiClock size={14} /> {formatDuration(lecture.startTime, lecture.endTime)}
                      </span>
                    </div>
                    <div className="lecture-batch">
                      {lecture.batch?.batchName || 'Unassigned batch'} ·{' '}
                      {lecture.batch?.courseTitle || 'Course TBC'}
                    </div>
                    {lecture.moduleTitle && <div className="lecture-module">Module · {lecture.moduleTitle}</div>}
                    {lecture.recordedAt && (
                      <div className="lecture-recorded">Marked at {formatDateTime(lecture.recordedAt)}</div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </section>

      {error && <div className="student-error">{error}</div>}
    </div>
  )
}

export default StudentDetail
